import React, { useEffect, useState } from "react";
import {
    Grid,
    IconButton,
    TextField,
    MenuItem,
    FormControl,
    Select,
    Typography,
} from "@mui/material";
import { FastField, Field, useFormikContext } from "formik";
import DeleteIcon from "@mui/icons-material/Delete";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import { getCatalogue, getCatalogueEntryById } from "../../api/common/catalogues.ts";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { LGNumber } from "../../atoms/Inputs/LGNumber.tsx";
import { getAssetList } from "../../api/assets/index.js";
import { formatCurrency } from "../../services/valueFormatters";

const POItemRowService = ({ index, remove, expenseAccounts, companyId, units, assetTypeOptions }) => {
    const { setFieldValue, values, touched, errors } = useFormikContext();
    const { openSnack } = useSnack();
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [destinationOptions, setDestinationOptions] = useState([]);


    const fetchDestinations = async (assetTypeId) => {
        if (!assetTypeId) {
            setDestinationOptions([]);
            return;
        }
        try {
            const res = await getCatalogue({ CatalogueName: assetTypeId });
            setDestinationOptions(res.data);
        } catch (e) {
            openSnack(handleError(e), "error");
        }
    };

    const handleUnitChange = async (e, value) => {
        const unitId = value.id;
        setFieldValue(`items.${index}.unitId`, unitId);
        try {
            const unitData = await getCatalogueEntryById(unitId);
            setSelectedUnit(unitData.data);
        } catch (e) {
            openSnack(handleError(e), "error");
        }
    };

    const handleAssetTypeChange = (event, option) => {
        const assetTypeId = option?.id;
        setFieldValue(`items.${index}.assetTypeCode`, option.code);
        fetchDestinations(option.code);
    };

    useEffect(() => {
        const discountPercentage = Number(values.items[index].discountPercentage) || 0;
        if (selectedUnit?.code === "millar") {
            const quantity = values.items[index].quantity;
            const price = values.items[index].price / 1000;
            const subtotal = quantity * price * (1 - (discountPercentage / 100));
            setFieldValue(`items.${index}.subtotal`, subtotal);
        }
        else {
            const quantity = values.items[index].quantity;
            const price = values.items[index].price;
            const subtotal = quantity * price * (1 - (discountPercentage / 100));
            setFieldValue(`items.${index}.subtotal`, subtotal);
        }
    }, [values.items[index].quantity, values.items[index].price, values.items[index].discountPercentage, selectedUnit]);

    return (
        <Grid container spacing={0}>
            <Grid item xs={1}>
                <FastField
                    as={DropdownSelector}
                    parentOptions={expenseAccounts}
                    optionLabel={["completeContpaqiCode", "name"]}
                    optionSeparator=" | "
                    name={`items.${index}.expenseAccountId`}
                    fullWidth
                    size="small"
                    margin="none"
                />
            </Grid>
            <Grid item xs={1}>
                <FastField
                    as={TextField}
                    name={`items.${index}.quantity`}
                    fullWidth
                    size="small"
                    margin="none"
                    type="number"
                />
            </Grid>
            <Grid item xs={1}>
                <FastField
                    as={DropdownSelector}
                    parentOptions={units}
                    optionLabel="code"
                    name={`items.${index}.unitId`}
                    fullWidth
                    size="small"
                    margin="none"
                    onChange={handleUnitChange}
                />
            </Grid>
            <Grid item xs={2}>
                <FastField
                    as={TextField}
                    name={`items.${index}.concept`}
                    fullWidth
                    multiline
                    size="small"
                    margin="none"
                />
            </Grid>
            <Grid item xs={1}>
                <FastField
                    as={DropdownSelector}
                    parentOptions={assetTypeOptions}
                    optionLabel="name"
                    name={`items.${index}.assetTypeId`}
                    onChange={handleAssetTypeChange}
                    fullWidth
                    size="small"
                    margin="none"
                />
            </Grid>
            <Grid item xs={1}>

                <Field
                    as={DropdownSelector}
                    fetchFunction={getAssetList}
                    fetchParams={{ 
                        companyId: companyId, 
                        assetTypeId: values.items[index].assetTypeId ,
                        pageSize: 1000
                    }}
                    optionLabel={["name", "code"]}
                    optionSeparator=" | "
                    name={`items.${index}.assetId`}
                    fullWidth
                    size="small"
                    margin="none"
                />
            </Grid>
            <Grid item xs={1}>
                <FastField
                    as={LGNumber}
                    name={`items.${index}.price`}
                    fullWidth
                    size="small"
                    margin="none"
                />
            </Grid>
            <Grid item xs={1}>
                <FastField
                    as={TextField}
                    name={`items.${index}.discountPercentage`}
                    fullWidth
                    size="small"
                    margin="none"
                    type="number"
                />
            </Grid>
            <Grid item xs={1} sx={{ textAlign: "center", justifyContent: "center" }}>
                <Typography variant="body2">
                    {formatCurrency(Number(values.items[index].subtotal) || 0)}
                </Typography>
            </Grid>
            <Grid item xs={1}>
                <FastField name={`items.${index}.taxRate`}>
                    {({ field }) => (
                        <FormControl fullWidth size="small" margin="none" variant="outlined">
                            <Select {...field} label="Tax Rate">
                                {[0.0, 8.0, 16.0].map((rate) => (
                                    <MenuItem key={rate} value={rate}>
                                        {rate}%
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </FastField>
            </Grid>
            <Grid item xs={1}>
                <IconButton color="error" onClick={() => remove(index)}>
                    <DeleteIcon />
                </IconButton>
            </Grid>
        </Grid>
    );
};

export default POItemRowService;
